import "@/App.css";
import { useEffect, useState, useRef } from "react";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, useLocation, Navigate, Link, useParams } from "react-router-dom";
import { AppProvider, useApp } from "@/context/AppContext";
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/Header";
import PendingBanner from "@/components/PendingBanner";
import OnboardingModal from "@/components/OnboardingModal";
import LocationPrompt from "@/components/LocationPrompt";
import HeartLoader from "@/components/HeartLoader";
import { Heart } from "lucide-react";
import InstallPWA from "@/components/InstallPWA";
import { ShantiMantra } from "@/components/ShantiMantra";
import AnXya from "@/components/AnXya";
import { RechargeModal } from "@/components/RechargeModal";
import { GuruDakshina } from "@/components/GuruDakshina";
import Landing from "@/pages/Landing";
import Engine from "@/pages/Engine";
import Personal from "@/pages/Personal";
import Professional from "@/pages/Professional";
import Directory from "@/pages/Directory";
import Feed from "@/pages/Feed";
import BusinessProfile from "@/pages/BusinessProfile";
import Learn from "@/pages/Learn";
import Vault from "@/pages/Vault";
import Search from "@/pages/Search";
import Showcase from "@/pages/Showcase";
import Btc from "@/pages/Btc";

import Profile from "@/pages/Profile";
import BusinessInsights from "@/pages/BusinessInsights";
import PublicStory from "@/pages/PublicStory";
import Economy from "@/pages/Economy";
import Prosperity from "@/pages/Prosperity";
import Ventures from "@/pages/Ventures";
import Affiliate from "@/pages/Affiliate";
import SecureMeet from "@/pages/SecureMeet";
import MissionTransport from "@/pages/MissionTransport";
import StayDetail from "@/pages/StayDetail";
import UCB from "@/pages/UCB";
import LockDashboard from "@/pages/LockDashboard";
import Insights from "@/pages/Insights";
import FinanceHub from "@/pages/FinanceHub";
import PublicSignals from "@/pages/PublicSignals";
import About from "@/pages/About";
import Compliance from "@/pages/Compliance";
import Galactopedia from "@/pages/Galactopedia";
import Auth, { AdminLoginPage } from "@/pages/Auth";
import SmartDrive from "@/pages/SmartDrive";
import DriveShare from "@/pages/DriveShare";
import Chat from "@/pages/Chat";
import TeacherStudio from "@/pages/TeacherStudio";
import Teachers from "@/pages/Teachers";
import Events from "@/pages/Events";
import Meals from "@/pages/Meals";
import News from "@/pages/News";
import SavedNews from "@/pages/SavedNews";
import AdminAccess from "@/pages/AdminAccess";
import MarketingConsole from "@/pages/MarketingConsole";
import FeaturePage from "@/pages/FeaturePage";
import InviteConnections from "@/pages/InviteConnections";
import PublicProfile from "@/pages/PublicProfile";
import PublicEvent from "@/pages/PublicEvent";
import Legal from "@/pages/Legal";
import DataRights from "@/pages/DataRights";
import CookieConsent from "@/components/CookieConsent";
import TermsGate from "@/components/TermsGate";
import CreditsHistory from "@/pages/CreditsHistory";
import Watch from "@/pages/Watch";
import GetApp from "@/pages/GetApp";
import Investors from "@/pages/Investors";
import Practice from "@/pages/Practice";
import Bookings from "@/pages/Bookings";
import AuthCallback from "@/pages/AuthCallback";

const UNIVERSE_BG = "https://customer-assets-v7afamib.emergentagent.net/job_6dfaf7ec-672f-421f-93af-1927e0557ba9/artifacts/omso9s9o_gjwF3CaJSee2Hr3LUodYyJ-1000-80.jpg.webp";

function CompanyRedirect() {
  const { id } = useParams();
  return <Navigate to={`/businesses/${id}`} replace />;
}

// Gates "God Mode" pages behind authentication — shows the Lock Dashboard until connected.
function ProtectedRoute({ children }) {
  const { user } = useApp();
  if (user === undefined) {
    return <div className="min-h-[70vh] grid place-items-center" data-testid="auth-loading"><HeartLoader label="Loading…" /></div>;
  }
  if (!user) return <LockDashboard />;
  return children;
}

function BackgroundLayer() {
  const { bgImage } = useApp();
  const src = bgImage || UNIVERSE_BG;
  return (
    <div className="fixed inset-0 -z-10 pointer-events-none">
      <img src={src} alt="" className="bg-universe-img w-full h-full object-cover" />
      <div className="bg-universe-overlay absolute inset-0" />
    </div>
  );
}

function Footer() {
  const { country } = useApp();
  const isIndia = String(country || "").toUpperCase() === "IN";
  const [showQr, setShowQr] = useState(false);
  return (
    <footer className="border-t border-rud mt-10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 text-center text-sm text-sec">
        <div className="mb-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs uppercase tracking-[0.18em] text-[var(--rud-text)]" data-testid="footer-principles">
          <span>Decentralized</span><span className="text-brand">·</span>
          <span>Democratic</span><span className="text-brand">·</span>
          <span>Distributed</span><span className="text-brand">·</span>
          <span>Data-Sovereign</span>
        </div>
        <p className="max-w-3xl mx-auto text-sm leading-relaxed mb-6" data-testid="footer-mission">
          My Kind is <span className="text-[var(--rud-text)] font-medium">AI-native and 6th-generation — self-evolving software that improves lives.</span>{" "}
          It spreads love and kindness, dissolves division across borders, colours and race, and lets us live as one family —
          learning, earning, sharing resources and having fun as we grow together and all benefit from the network.
        </p>
        <div className="max-w-lg mx-auto mb-7"><ShantiMantra compact /></div>
        <div className="mb-6">
          <a href="https://razorpay.me/@anxya" target="_blank" rel="noopener noreferrer" data-testid="footer-razorpay-support"
            className="btn-primary inline-flex items-center gap-2 rounded-full h-10 px-6 text-sm">
            <Heart size={15} className="fill-current" /> Support My Kind
          </a>
          <p className="text-xs text-sec mt-2">Love what we're building? Chip in securely via Razorpay (UPI / cards) 💛</p>
          <button data-testid="footer-upi-qr-toggle" onClick={() => setShowQr((v) => !v)}
            className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[var(--rud-text)] hover:text-brand transition-colors">
            <Heart size={14} className="fill-current text-brand" /> {showQr ? "Hide UPI QR" : "Or pay with UPI QR code"}
          </button>
          {showQr && (
            <div className="mt-3 mx-auto w-fit rounded-2xl border border-rud bg-white p-4 flex flex-col items-center" data-testid="footer-upi-qr">
              <img src="/upi-qr.png" alt="ANXYA Tech UPI QR — scan & pay with any UPI app" className="w-52 h-auto rounded-lg" data-testid="footer-upi-qr-img" />
              <p className="text-xs text-neutral-800 text-center mt-2 max-w-[260px] leading-snug">Scan with any UPI app to support <b>ANXYA Tech Private Limited</b> 💛</p>
            </div>
          )}
        </div>
        <div className="mb-3">
          <Link to="/download" data-testid="footer-get-app" className="inline-flex items-center gap-2 text-brand hover:underline font-medium">
            📲 Get the My Kind app — share with your family
          </Link>
          <span className="mx-2 text-brand">·</span>
          <Link to="/investors" data-testid="footer-investors" className="inline-flex items-center gap-2 text-[var(--rud-text)] hover:text-brand transition-colors font-medium">
            Investor brief
          </Link>
        </div>
        <div className="mb-3">
          <Link to="/galactopedia" data-testid="footer-galactopedia" className="text-[var(--rud-text)] hover:text-brand transition-colors font-medium">
            Galactopedia — the collective brain of the community
          </Link>
        </div>
        <div className="mb-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs">
          <Link to="/privacy" className="hover:text-brand transition-colors">Privacy</Link><span className="text-brand">·</span>
          <Link to="/terms" className="hover:text-brand transition-colors">Terms</Link><span className="text-brand">·</span>
          <Link to="/data-rights" data-testid="footer-data-rights" className="hover:text-brand transition-colors font-medium text-[var(--rud-text)]">Privacy &amp; Your Data{isIndia ? " (DPDP)" : ""}</Link>
        </div>
        © Copyright{" "}
        <a href="https://www.anxya.tech" target="_blank" rel="noopener noreferrer" data-testid="footer-company"
          className="text-[var(--rud-text)] hover:text-brand transition-colors">Anxya Tech Private Limited 2026</a>.{" "}
        <Link to="/about" data-testid="footer-anxya-story" className="hover:text-brand transition-colors">
          (THE My Kind STORY — Built by a Yogi in Karm Sanyas — dedicating life for society, mankind, selflessly)
        </Link>
      </div>
    </footer>
  );
}

function RouteTransitionHeart() {
  const location = useLocation();
  const [show, setShow] = useState(false);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    setShow(true);
    const t = setTimeout(() => setShow(false), 550);
    return () => clearTimeout(t);
  }, [location.pathname]);
  if (!show) return null;
  return (
    <div className="fixed inset-0 z-[9998] grid place-items-center pointer-events-none"
      style={{ background: "rgba(11,11,15,0.55)", backdropFilter: "blur(2px)" }} data-testid="route-transition-heart">
      <HeartLoader size={56} />
    </div>
  );
}

function Shell() {
  const location = useLocation();
  const { user } = useApp();
  useEffect(() => {
    if (!location.hash) window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.search, location.hash]);
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  if (location.pathname === "/investors" || location.pathname === "/pitch") {
    return <Investors />;
  }
  const isAuthPage = location.pathname === "/auth";
  const isAdminPage = location.pathname.startsWith("/admin");
  return (
    <>
      <BackgroundLayer />
      {!isAuthPage && <Header />}
      <RouteTransitionHeart />
      {!isAuthPage && <PendingBanner />}
      <InstallPWA />
      <CookieConsent />
      {!isAdminPage && <TermsGate />}
      <main className="min-h-screen">
        <Routes>
          <Route path="/privacy" element={<Legal mode="privacy" />} />
          <Route path="/data-rights" element={<DataRights />} />
          <Route path="/terms" element={<Legal mode="terms" />} />
          <Route path="/" element={
            user === undefined
              ? <div className="min-h-[70vh] grid place-items-center" data-testid="home-auth-loading"><HeartLoader label="Loading…" /></div>
              : user
                ? <Navigate to="/personal?tab=social" replace />
                : <Landing />
          } />
          <Route path="/engine" element={<Engine />} />
          <Route path="/compliance" element={<Compliance />} />
          <Route path="/galactopedia" element={<Galactopedia />} />
          <Route path="/personal" element={<Personal />} />
          <Route path="/professional" element={<Professional />} />
          <Route path="/businesses" element={<Directory />} />
          <Route path="/ventures" element={<Ventures />} />
          <Route path="/affiliate" element={<Affiliate />} />
          <Route path="/meet" element={<SecureMeet />} />
          <Route path="/transport" element={<MissionTransport />} />
          <Route path="/stays/:id" element={<StayDetail />} />
          <Route path="/rides" element={<Navigate to="/transport" replace />} />
          <Route path="/businesses/:id" element={<BusinessProfile />} />
          <Route path="/companies" element={<Navigate to="/businesses" replace />} />
          <Route path="/companies/:id" element={<CompanyRedirect />} />
          <Route path="/feed" element={<Feed />} />
          <Route path="/learn" element={<Learn />} />
          <Route path="/money" element={<Navigate to="/" replace />} />
          <Route path="/bank" element={<Navigate to="/" replace />} />
          <Route path="/signals" element={<PublicSignals />} />
          <Route path="/interpret" element={<Navigate to="/personal" replace />} />
          <Route path="/vault" element={<Vault />} />
          <Route path="/search" element={<Search />} />
          <Route path="/community" element={<Navigate to="/personal?tab=community" replace />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/business-insights" element={<BusinessInsights />} />
          <Route path="/story/:slug" element={<PublicStory />} />
          <Route path="/economy" element={<Economy />} />
          <Route path="/prosperity" element={<ProtectedRoute><Prosperity /></ProtectedRoute>} />
          <Route path="/btc" element={<Btc />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/about" element={<About />} />
          <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
          <Route path="/teach" element={<ProtectedRoute><TeacherStudio /></ProtectedRoute>} />
          <Route path="/teachers" element={<Teachers />} />
          <Route path="/events" element={<Events />} />
          <Route path="/meals" element={<Meals />} />
          <Route path="/news" element={<News />} />
          <Route path="/admin/login" element={<Navigate to="/" replace />} />
          <Route path="/admin/portal/:key" element={<AdminLoginPage />} />
          <Route path="/AadyaNitya@0308@1236@1369" element={<AdminLoginPage />} />
          <Route path="/admin/access" element={<AdminAccess />} />
          <Route path="/admin/marketing" element={<MarketingConsole />} />
          <Route path="/drive" element={<ProtectedRoute><SmartDrive /></ProtectedRoute>} />
          <Route path="/drive/s/:token" element={<DriveShare />} />
          <Route path="/invite" element={<ProtectedRoute><InviteConnections /></ProtectedRoute>} />
          <Route path="/u/:userId" element={<PublicProfile />} />
          <Route path="/e/:id" element={<PublicEvent />} />
          <Route path="/saved" element={<SavedNews />} />
          <Route path="/practice" element={<Practice />} />
          <Route path="/wallet" element={<CreditsHistory />} />
          <Route path="/watch" element={<Watch />} />
          <Route path="/download" element={<GetApp />} />
          <Route path="/get-app" element={<GetApp />} />
          <Route path="/bookings" element={<ProtectedRoute><Bookings /></ProtectedRoute>} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/f/:slug" element={<FeaturePage />} />
        </Routes>
      </main>
      {!isAuthPage && <Footer />}
      {!isAuthPage && <AnXya />}
      <RechargeModal />
      <GuruDakshina />
      {!isAuthPage && <OnboardingModal />}
      {!isAuthPage && !isAdminPage && <LocationPrompt />}
      <Toaster position="top-right" theme="dark" />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AppProvider>
          <Shell />
        </AppProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
